import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { programs } from '@/mocks/neurobaby';

export default function ConoceElMetodo() {
  const routeMap: Record<number, string> = {
    1: '/fundamentos-sueno',
    2: '/yoga-sueno-profundo',
    3: '/programa-completo-neurobabys',
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      {/* Hero + Carta */}
      <section className="pt-14 md:pt-20 px-6 md:px-10 pb-8 bg-white">
        <div className="w-full max-w-4xl mx-auto">
          <div className="relative rounded-3xl bg-cream border border-sand/60 overflow-hidden">
            {/* franja decorativa superior */}
            <div className="h-1.5 w-full bg-gradient-to-r from-transparent via-terracotta to-transparent" />

            <div className="px-6 md:px-12 py-9 md:py-12">
              {/* Encabezado */}
              <div className="text-center">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-terracotta/15 text-terracotta mb-5">
                  <i className="ri-moon-clear-line text-xl" />
                </span>
                <div>
                  <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.25em]">
                    NeuroBabys
                  </span>
                </div>
                <h1 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-[#8B6F5C] leading-tight mt-2 mb-3">
                  Conocé el método
                </h1>
                <p className="font-serif text-lg md:text-xl text-terracotta italic leading-snug">
                  Tres caminos. Un mismo objetivo: acompañar a tu bebé con comprensión.
                </p>
              </div>

              {/* Divisor decorativo */}
              <div className="flex items-center justify-center gap-3 my-7">
                <span className="h-px w-14 md:w-20 bg-sand" />
                <span className="w-2 h-2 rounded-full bg-terracotta" />
                <span className="h-px w-14 md:w-20 bg-sand" />
              </div>

              {/* Carta */}
              <div className="w-full max-w-2xl mx-auto text-left">
                <div className="font-serif text-base text-[#3D2B1F] leading-relaxed space-y-3">
                  <p>
                    Cada programa está pensado para una etapa diferente del sueño infantil. Elegí el que
                    mejor se adapte a lo que estás viviendo hoy, o empezá por el completo si querés tener
                    todas las herramientas.{' '}
                    <span className="italic text-[#8B6F5C]">Quiero que elijas con calma.</span>
                  </p>
                  <p>
                    No hay un programa mejor que otro: hay uno que puede acompañarte mejor en el momento
                    que vos y tu bebé están viviendo hoy.
                  </p>
                  <p>
                    Preparé estos tres caminos para que puedas empezar desde donde necesitás, sin apuro y
                    sin sentir que tenés que saberlo todo de antemano.
                  </p>
                  <p>
                    Podés comenzar por lo esencial, profundizar con yoga y regulación, o elegir el
                    recorrido completo para tener todas las herramientas a tu alcance durante cada etapa.
                  </p>
                  <p>
                    Mi deseo es que encuentres acá algo más que información: que encuentres comprensión,
                    alivio y una forma más serena de acompañar a tu bebé.
                  </p>
                </div>

                <div className="mt-5 pt-4 border-t border-sand/50">
                  <p className="font-serif text-base text-[#8B6F5C] italic">Con cariño,</p>
                  <p className="font-serif text-lg font-bold text-[#8B6F5C] mt-1">Mercedes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programas horizontales */}
      <section className="px-6 md:px-10 pb-16 md:pb-24">
        <div className="w-full max-w-5xl mx-auto flex flex-col gap-6">
          {programs.map((program, index) => (
            <div
              key={program.id}
              className={`grid grid-cols-1 md:grid-cols-[2fr_3fr] items-stretch rounded-2xl overflow-hidden border border-sand/40 ${
                index % 2 === 0 ? 'bg-white' : 'bg-cream'
              }`}
            >
              {/* Imagen */}
              <div className="relative overflow-hidden aspect-[16/10] md:aspect-auto md:min-h-[240px]">
                <img
                  src={program.image}
                  alt={program.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Texto */}
              <div className="flex flex-col justify-center p-5 md:p-8">
                <span className="font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-2">
                  {program.subtitle}
                </span>
                <h2 className="font-serif text-xl md:text-2xl font-bold text-[#8B6F5C] leading-tight mb-2">
                  {program.title}
                </h2>
                <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed mb-3">
                  {program.description}
                </p>
                <div className="flex items-center gap-4 text-sm text-[#5C4A3A] mb-4">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line text-xs" />
                    {program.duration}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-book-open-line text-xs" />
                    {program.lessons} guías
                  </span>
                  <span className="bg-cream border border-sand/50 px-2.5 py-0.5 rounded-full font-sans text-xs font-semibold text-[#8B6F5C]">
                    {program.level}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-sand/30">
                  <div className="flex items-baseline gap-1">
                    <span className="font-sans text-2xl md:text-3xl font-bold text-[#8B6F5C]">
                      ${program.price}
                    </span>
                    <span className="font-sans text-xs text-[#5C4A3A]">USD</span>
                  </div>
                  <Link
                    to={routeMap[program.id]}
                    className="inline-flex items-center gap-1.5 bg-terracotta text-white px-5 py-2.5 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap"
                  >
                    Conocer más
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}